import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

// Connects to data-controller="detail-chart"
// Renders a combo chart: 2 bar datasets (left Y) + 2 line datasets (right Y)
// Smart Y-axis: caps at the 90th percentile so outliers don't squash other bars.
// Clipped bars show a diagonal stripe pattern. Toggle to full scale via button.
export default class extends Controller {
  static targets = ["canvas", "scaleToggle"];
  static values = {
    chartData: Array,
    plannedLabel: String,
    actualLabel: String,
    qtyPerM2Label: String,
    logCountLabel: String,
  };

  connect() {
    this.smartScale = true;
    this.renderChart();
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  toggleScale() {
    this.smartScale = !this.smartScale;
    if (this.chart) this.chart.destroy();
    this.renderChart();

    // Update toggle button label (target the span, not the button, to preserve the icon)
    if (this.hasScaleToggleTarget) {
      const label = this.scaleToggleTarget.querySelector("span");
      if (label) {
        label.textContent = this.smartScale
          ? this.scaleToggleTarget.dataset.smartLabel
          : this.scaleToggleTarget.dataset.fullLabel;
      }
    }
  }

  renderChart() {
    const ctx = this.canvasTarget.getContext("2d");
    const data = this.chartDataValue;
    const labels = data.map((d) => d.label);

    // Compute smart Y-axis cap (90th percentile of bar values)
    const allBarValues = [
      ...data.map((d) => d.planned_qty),
      ...data.map((d) => d.actual_qty),
    ]
      .filter((v) => v > 0)
      .sort((a, b) => a - b);

    let yMax = undefined;
    if (this.smartScale && allBarValues.length > 2) {
      const p90Index = Math.floor((allBarValues.length - 1) * 0.9);
      const p90 = allBarValues[p90Index];
      const maxVal = allBarValues[allBarValues.length - 1];

      // Only cap if the max is more than 3x the 90th percentile
      if (maxVal > p90 * 3) {
        yMax = Math.ceil(p90 * 1.5);
      }
    }

    // Build bar colors — striped pattern for values exceeding the smart cap
    let plannedColors, actualColors;
    if (yMax) {
      const clippedPattern = this.#createStripedPattern(
        ctx,
        "#93c5fd",
        "#dbeafe",
      );
      const clippedPatternDark = this.#createStripedPattern(
        ctx,
        "#2563eb",
        "#93c5fd",
      );
      plannedColors = data.map((d) =>
        d.planned_qty > yMax ? clippedPattern : "#93c5fd",
      );
      actualColors = data.map((d) =>
        d.actual_qty > yMax ? clippedPatternDark : "#2563eb",
      );
    } else {
      plannedColors = "#93c5fd";
      actualColors = "#2563eb";
    }

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label: this.plannedLabelValue,
            data: data.map((d) => d.planned_qty),
            backgroundColor: plannedColors,
            borderColor: "#93c5fd",
            borderWidth: 1,
            yAxisID: "y",
            order: 3,
          },
          {
            type: "bar",
            label: this.actualLabelValue,
            data: data.map((d) => d.actual_qty),
            backgroundColor: actualColors,
            borderColor: "#2563eb",
            borderWidth: 1,
            yAxisID: "y",
            order: 2,
          },
          {
            type: "line",
            label: this.qtyPerM2LabelValue,
            data: data.map((d) => d.qty_per_m2),
            borderColor: "#22c55e",
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: false,
            yAxisID: "y1",
            order: 1,
          },
          {
            type: "line",
            label: this.logCountLabelValue,
            data: data.map((d) => d.log_count),
            borderColor: "#8b5cf6",
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: false,
            yAxisID: "y",
            order: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 9 }, maxRotation: 45, minRotation: 25 },
          },
          y: {
            type: "linear",
            position: "left",
            beginAtZero: true,
            max: yMax,
            title: {
              display: true,
              text:
                this.plannedLabelValue +
                " / " +
                this.actualLabelValue +
                " / " +
                this.logCountLabelValue,
              font: { size: 10 },
            },
            ticks: { font: { size: 10 } },
            grid: { color: "#f0f0f0" },
          },
          y1: {
            type: "linear",
            position: "right",
            beginAtZero: true,
            title: {
              display: true,
              text: this.qtyPerM2LabelValue,
              font: { size: 10 },
            },
            ticks: { font: { size: 10 } },
            grid: { display: false },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              boxWidth: 10,
              font: { size: 10 },
              usePointStyle: true,
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || "";
                const value = context.parsed.y;
                if (value == null) return `${label}: -`;
                // Show real value from raw data for clipped bars
                const raw = context.raw;
                const suffix = yMax && raw > yMax ? " ⚠" : "";
                return `${label}: ${raw.toLocaleString()}${suffix}`;
              },
            },
          },
        },
      },
    });
  }

  // Creates a diagonal stripe canvas pattern for clipped bars
  #createStripedPattern(ctx, color1, color2) {
    const patternCanvas = document.createElement("canvas");
    patternCanvas.width = 10;
    patternCanvas.height = 10;
    const pCtx = patternCanvas.getContext("2d");

    pCtx.fillStyle = color1;
    pCtx.fillRect(0, 0, 10, 10);
    pCtx.strokeStyle = color2;
    pCtx.lineWidth = 3;
    pCtx.beginPath();
    pCtx.moveTo(0, 10);
    pCtx.lineTo(10, 0);
    pCtx.stroke();

    return ctx.createPattern(patternCanvas, "repeat");
  }
}
